///
/// EarthBrowser
///
/// Redux Persist Migrations
///
/// This file exports all migration functions in order.
/// Each migration transforms the persisted state from one version to the next.
///

import { migrateV10toV11 } from "./v10_to_v11_add_geopackage_state"
import { migrateV11toV12 } from "./v11_to_v12_add_sql_history"
import { migrateV12toV13 } from "./v12_to_v13_add_minimized_tile_positions"
import { migrateV13toV14 } from "./v13_to_v14_add_persisted_window_states"
import { migrateV14toV15 } from "./v14_to_v15_add_python_history"
import { migrateV15toV16 } from "./v15_to_v16_add_geopackage_download_directory"
import { migrateV16toV17 } from "./v16_to_v17_add_local_data_directory"
import { migrateV17toV18 } from "./v17_to_v18_add_git_config"
import { migrateV19toV20 } from "./v19_to_v20_add_github_oauth"
import { migrateV1toV2 } from "./v1_to_v2_remove_flight_dataset"
import { migrateV20toV21 } from "./v20_to_v21_add_dock_mode"
import { migrateV21toV22 } from "./v21_to_v22_add_bottom_drawer_width"
import { migrateV22toV23 } from "./v22_to_v23_add_city_weather_dataset"
import { migrateV23ToV24 } from "./v23_to_v24_add_synoptic_settings"
import { migrateV24toV25 } from "./v24_to_v25_add_radar_visible"
import { migrateV25toV26 } from "./v25_to_v26_add_webcam_dataset"
import { migrateV26toV27 } from "./v26_to_v27_add_wildfire_dataset"
import { migrateV27toV28 } from "./v27_to_v28_add_hurricane_dataset"
import { migrateV28toV29 } from "./v28_to_v29_remove_tool_dock"
import { migrateV29toV30 } from "./v29_to_v30_unify_dataset_visibility"
import { migrateV30toV31 } from "./v30_to_v31_remove_minimized_tiles"
import { migrateV31toV32 } from "./v31_to_v32_remove_command_palette_open"
import { migrateV32toV33 } from "./v32_to_v33_add_location_visibility"
import { migrateV33toV34 } from "./v33_to_v34_add_borders_dataset"
import { migrateV34toV35 } from "./v34_to_v35_add_saved_views"
import { migrateV35toV36 } from "./v35_to_v36_add_tour_settings"
import { migrateV36toV37 } from "./v36_to_v37_add_engagement"
import { migrateV37toV38 } from "./v37_to_v38_add_conversion_prompts"
import { migrateV38toV39 } from "./v38_to_v39_add_saved_view_prompt"
import { migrateV39toV40 } from "./v39_to_v40_add_offer_snooze"
import { migrateV40toV41 } from "./v40_to_v41_add_radar_dataset"
import { migrateV41toV42 } from "./v41_to_v42_add_sst_dataset"
import { migrateV42toV43 } from "./v42_to_v43_add_alert_dataset"
import { migrateV43toV44 } from "./v43_to_v44_add_drawer_active_tab"
import { migrateV44toV45 } from "./v44_to_v45_add_placemarks"
import { migrateV45toV46 } from "./v45_to_v46_add_recent_commands"
import { migrateV46toV47 } from "./v46_to_v47_add_stories"
import { migrateV47toV48 } from "./v47_to_v48_add_story_datasets"
import { migrateV48toV49 } from "./v48_to_v49_add_clock_playback_options"
import { migrateV49toV50 } from "./v49_to_v50_remove_clock_playback_options"
import { migrateV50toV51 } from "./v50_to_v51_add_terrain_settings"
import { migrateV51toV52 } from "./v51_to_v52_remove_terrain_settings"
import { migrateV52toV53 } from "./v52_to_v53_add_synoptic_sublayers"
import { migrateV53toV54 } from "./v53_to_v54_add_dock_width"
import { migrateV54toV55 } from "./v54_to_v55_add_legends"
import { migrateV55toV56 } from "./v55_to_v56_legend_tray_toggle"
import { migrateV56toV57 } from "./v56_to_v57_add_layers_panel_groups"
import { migrateV57toV58 } from "./v57_to_v58_satellite_sets"
import { migrateV2toV3 } from "./v2_to_v3_add_aurora_palettes"
import { migrateV3toV4 } from "./v3_to_v4_fix_aurora_palettes"
import { migrateV5toV6 } from "./v5_to_v6_add_buoy_dataset"
import { migrateV6toV7 } from "./v6_to_v7_add_volcano_view_mode"
import { migrateV7toV8 } from "./v7_to_v8_satellite_group_id"
import { migrateV8toV9 } from "./v8_to_v9_add_location_focus_panel"
import { migrateV9toV10 } from "./v9_to_v10_rename_to_info_window"

/**
 * Type for a migration function
 * Takes state at version N and returns state at version N+1
 */
export type MigrationFunction = (state: unknown) => unknown

/**
 * Array of all migrations in order
 * Index 0 = migration from v0 to v1
 * Index 1 = migration from v1 to v2
 * etc.
 *
 * To add a new migration:
 * 1. Create a new file: vN_to_vM_description.ts
 * 2. Export a migration function from that file
 * 3. Add it to this array
 * 4. Update CURRENT_VERSION in store.ts
 * 5. Document the migration in MIGRATIONS.md
 */
export const migrations: MigrationFunction[] = [
	// v0 to v1: No migration needed (initial version)
	(state: unknown) => state,

	// v1 to v2: Remove "flight" dataset type
	migrateV1toV2,

	// v2 to v3: Add aurora color palette fields
	migrateV2toV3,

	// v3 to v4: Fix aurora palette fields (re-apply in case v2→v3 failed due to mutation)
	migrateV3toV4,

	// v4 to v5: (removed - magnetic field feature removed)
	(state: unknown) => state,

	// v5 to v6: Add buoy dataset support
	migrateV5toV6,

	// v6 to v7: Add volcano view mode preference
	migrateV6toV7,

	// v7 to v8: Convert satelliteRequestParams to selectedSatelliteGroupId
	migrateV7toV8,

	// v8 to v9: Add location focus panel state to userInterface
	migrateV8toV9,

	// v9 to v10: Rename locationFocusPanel to infoWindow with flexible content types
	migrateV9toV10,

	// v10 to v11: Add GeoPackage visibility state fields
	migrateV10toV11,

	// v11 to v12: Add SQL Terminal persistent command history
	migrateV11toV12,

	// v12 to v13: Add minimized window tile positions
	migrateV12toV13,

	// v13 to v14: Add persisted window states for restoration
	migrateV13toV14,

	// v14 to v15: Add Python Terminal persistent command history
	migrateV14toV15,

	// v15 to v16: Add GeoPackage download directory ID
	migrateV15toV16,

	// v16 to v17: Add local data directory and scan state
	migrateV16toV17,

	// v17 to v18: Add Git config state for remote repository configuration
	migrateV17toV18,

	// v18 to v19: (removed - file browser/terminal state removed with duckdb/
	// python/git support). Identity placeholder keeps array indices aligned
	// with version numbers: deleting it shifted every later migration down
	// one slot and left CURRENT_VERSION (39) below shipped clients' persisted
	// version (40), so newly appended migrations never ran.
	(state: unknown) => state,

	// v19 to v20: Add GitHub OAuth support for git operations
	migrateV19toV20,

	// v20 to v21: Add dock mode fields for bottom drawer docking
	migrateV20toV21,

	// v21 to v22: Add persisted bottom drawer width fields
	migrateV21toV22,

	// v22 to v23: Add city_weather dataset support
	migrateV22toV23,

	// v23 to v24: Add synoptic weather display settings
	migrateV23ToV24,

	// v24 to v25: Add radar visibility setting
	migrateV24toV25,

	// v25 to v26: Add webcam dataset support
	migrateV25toV26,

	// v26 to v27: Enable wildfire perimeter dataset by default
	migrateV26toV27,

	// v27 to v28: Enable hurricane / tropical cyclone dataset by default
	migrateV27toV28,

	// v28 to v29: Remove dead toolDock / context panel state
	migrateV28toV29,

	// v29 to v30: Fold cloudsVisible/radarVisible into visibleDatasets
	migrateV29toV30,

	// v30 to v31: Remove minimized tile positions and isMinimized flags
	migrateV30toV31,

	// v31 to v32: Remove commandPaletteOpen (palette merged into Layers panel)
	migrateV31toV32,

	// v32 to v33: City labels and label weather become toggleable, default on
	migrateV32toV33,

	// v33 to v34: Political borders vector layer becomes toggleable, default on
	migrateV33toV34,

	// v34 to v35: Add savedViews slice (saved camera views library)
	migrateV34toV35,

	// v35 to v36: Add tourSettings (camera director idle orbit)
	migrateV35toV36,

	// v36 to v37: Add engagement slice (anonymous session counting for the
	// account-conversion funnel)
	migrateV36toV37,

	// v37 to v38: Add conversion-prompt bookkeeping (interstitial/banner/chip)
	// to the engagement slice, absorbing the legacy support-nag localStorage keys
	migrateV37toV38,

	// v38 to v39: Add the one-time saved-view sync prompt flag to the
	// engagement slice
	migrateV38toV39,

	// v39 to v40: Add the full-access offer snooze timestamp to the
	// engagement slice
	migrateV39toV40,

	// v40 to v41: Split the global radar mosaic out of "precipitation" into
	// its own "radar" visibility toggle
	migrateV40toV41,

	// v41 to v42: Add the "sst" sea surface temperature dataset (default off)
	migrateV41toV42,

	// v42 to v43: Enable the weather alerts dataset by default
	migrateV42toV43,

	// v43 to v44: Persist the selected bottom drawer tab
	migrateV43toV44,

	// v44 to v45: Add placemarks slice (named places, 1..N locations each)
	migrateV44toV45,

	// v45 to v46: Add recentCommands to userInterface (persisted
	// recently-used layers panel commands)
	migrateV45toV46,

	// v46 to v47: Add stories slice (story tours: stop sequences with
	// narrative cards and globe annotations)
	migrateV46toV47,

	// v47 to v48: Add per-story cloud dataset reference pool (stops mount
	// referenced cloud datasets during playback)
	migrateV47toV48,

	// v48 to v49: Add clock playback options (loop, fit to data) and the
	// time zone display mode
	migrateV48toV49,

	// v49 to v50: Remove clockLoop / clockFitToData (looping is always on,
	// fit-to-data dropped with its UI); timeZoneMode stays
	migrateV49toV50,

	// v50 to v51: Add terrainSettings (vertical exaggeration, sunrise/sunset
	// relief light) for the Layers panel terrain controls
	migrateV50toV51,

	// v51 to v52: Remove terrainSettings (controls dropped; the engine uses
	// the fixed defaults of 1.2x exaggeration and sun relief light on)
	migrateV51toV52,

	// v52 to v53: Add "fronts" and "jetstream" synoptic sub-layer datasets
	// (default on, gated by the synoptic parent)
	migrateV52toV53,

	// v53 to v54: Add bottomDrawer.dockWidth (desktop browsers live in a
	// right-side data dock); drop the phone-only drawer's widthMode / width
	migrateV53toV54,

	// v54 to v55: Add userInterface.legends (legend tray: per-layer
	// expanded / chip / hidden state); drops the legacy per-legend
	// localStorage collapsed keys
	migrateV54toV55,

	// v55 to v56: Legends drop the per-layer "hidden" state (tray-level,
	// session-only hide instead); persisted "hidden" entries become chips
	migrateV55toV56,

	// v56 to v57: Add userInterface.layersPanel (collapsed catalog groups
	// in the Layers panel)
	migrateV56toV57,

	// v57 to v58: earthbrowser.selectedSatelliteGroupId (one raw CelesTrak
	// group) becomes earthbrowser.satelliteSets (multi-select curated sets)
	migrateV57toV58,
]

/**
 * Runs all migrations from the stored version to the current version
 * @param state - The persisted state
 * @param currentVersion - The version stored in the state (or 0 if new)
 * @param targetVersion - The version we want to migrate to
 * @returns The migrated state
 */
export function runMigrations(
	state: unknown,
	currentVersion: number,
	targetVersion: number
): unknown {
	if (!state) return state

	let migratedState: unknown = state

	// Run each migration in sequence
	for (let i = currentVersion; i < targetVersion; i++) {
		const migration = migrations[i]
		if (migration) {
			migratedState = migration(migratedState)
		}
	}

	return migratedState
}
